import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Alert,
    Divider,
    Chip
} from '@mui/material';
import { userAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { privilegePackages } from '../../config/privilege-packages';

const PrivilegeDialog = ({ open, onClose, user, modules }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [userPrivileges, setUserPrivileges] = useState({});

    useEffect(() => {
        if (user && open && modules) {
            const privileges = {};
            Object.keys(modules).forEach(moduleKey => {
                privileges[moduleKey] = [];
            });
            user.privileges?.forEach(priv => {
                if (privileges.hasOwnProperty(priv.module)) {
                    privileges[priv.module] = priv.operations;
                }
            });
            setUserPrivileges(privileges);
        }
    }, [user, open, modules]);

    const handlePrivilegeChange = (module, operation, checked) => {
        setUserPrivileges(prev => {
            const current = prev[module] || [];
            let updated = checked
                ? [...new Set([...current, operation])]
                : current.filter(op => op !== operation);

            const newPrivileges = { ...prev, [module]: updated };

            if (checked && module === 'appointments' && operation === 'create') {
                ['appointments', 'stock', 'scans'].forEach(depModule => {
                    if (!newPrivileges[depModule]) newPrivileges[depModule] = [];
                    if (!newPrivileges[depModule].includes('view')) {
                        newPrivileges[depModule] = [...newPrivileges[depModule], 'view'];
                    }
                });
            }
            return newPrivileges;
        });
    };

    const handleApplyPackage = (selectedPackage) => {
        if (!selectedPackage || !modules) return;
        const newPrivs = Object.keys(modules).reduce((acc, moduleKey) => {
            acc[moduleKey] = selectedPackage.privileges[moduleKey] || [];
            return acc;
        }, {});
        if (newPrivs.appointments?.includes('create')) {
            ['appointments', 'stock', 'scans'].forEach(depModule => {
                if (newPrivs[depModule]) {
                    newPrivs[depModule] = [...new Set([...newPrivs[depModule], 'view'])];
                }
            });
        }
        setUserPrivileges(newPrivs);
        toast.info(t('privileges.packageApplied', { packageName: selectedPackage.name }, t('privileges.packageApplied', 'Package applied')));
    };

    // ==================== START: THE FIX ====================
    // Reverted handleSave to use the required grant/revoke endpoints.
    // Enhanced with Promise.all for better performance.
    const handleSave = async () => {
        if (!user || !modules) return;

        setSaving(true);
        setError('');

        try {
            // Determine the full list of modules that need updating.
            // This includes modules that had privileges before and modules that will have them now.
            const modulesToProcess = new Set([
                ...Object.keys(userPrivileges),
                ...(user.privileges?.map(p => p.module) || [])
            ]);

            // Create an array of promises for all API calls
            const apiCallPromises = Array.from(modulesToProcess).map(moduleKey => {
                const newOperations = userPrivileges[moduleKey] || [];
                const allPossibleOpsForModule = modules[moduleKey]?.operations || ['view', 'create', 'update', 'delete'];

                if (newOperations.length > 0) {
                    // If there are operations selected for this module, grant them.
                    return userAPI.grantPrivileges(user.id, {
                        module: moduleKey,
                        operations: newOperations
                    });
                } else {
                    // If no operations are selected, revoke all possible privileges for this module.
                    return userAPI.revokePrivileges(user.id, {
                        module: moduleKey,
                        operations: allPossibleOpsForModule
                    });
                }
            });

            // Execute all API calls in parallel for maximum speed
            await Promise.all(apiCallPromises);

            toast.success('Privileges updated successfully');
            onClose(true); // Signal a refresh
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to update privileges';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };
    // ===================== END: THE FIX =====================

    const handleClose = () => {
        if (!saving) {
            setError('');
            setUserPrivileges({});
            onClose();
        }
    };

    if (!user) return null;

    if (!modules || typeof modules !== 'object' || Object.keys(modules).length === 0) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">{t('privileges.managePrivilegesFor', { username: user?.username || '' })}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning">{t('privileges.noModules', 'Privilege modules are not available or are still loading.')}</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">{t('common.close')}</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6">{t('privileges.managePrivilegesFor', { username: user.username })}</Typography>
                <Typography variant="body2" color="textSecondary">{user.email}</Typography>
            </DialogTitle>

            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                ) : (
                    <Box>
                        <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{t('privileges.applyPackage', 'Apply a Package')}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {privilegePackages.map(pkg => (
                                    <Button key={pkg.value} onClick={() => handleApplyPackage(pkg)} variant="outlined" size="small" disabled={saving}>
                                        {pkg.name}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{t('privileges.selectOperations', 'Or, select operations individually:')}</Typography>
                        {Object.entries(modules).map(([moduleKey, moduleInfo]) => {
                            const currentPrivileges = userPrivileges[moduleKey] || [];
                            if (!Array.isArray(moduleInfo.operations)) return null;
                            return (
                                <Box key={moduleKey} sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{t(`modules.${moduleKey}`, moduleKey)}</Typography>
                                        <Chip label={t(`modules.${moduleKey}_desc`, moduleInfo.description)} size="small" sx={{ ml: 1 }} variant="outlined" />
                                    </Box>
                                    <FormGroup row>
                                        {moduleInfo.operations.map(operation => (
                                            <FormControlLabel
                                                key={operation}
                                                control={<Checkbox checked={currentPrivileges.includes(operation)} onChange={(e) => handlePrivilegeChange(moduleKey, operation, e.target.checked)} disabled={saving} />}
                                                label={t(`operations.${operation}`, operation)}
                                                sx={{ minWidth: 120, textTransform: 'capitalize' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Divider sx={{ mt: 1 }} />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button onClick={handleClose} disabled={saving} variant="outlined">{t('common.cancel')}</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || loading} startIcon={saving ? <CircularProgress size={20} /> : null}>
                    {saving ? t('common.processing') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrivilegeDialog;